import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, FloppyDisk, User as UserIcon, UploadSimple, Trash, Buildings, Gift } from "@phosphor-icons/react";

export default function Profile() {
    const nav = useNavigate();
    const { user, refresh } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [apellidos, setApellidos] = useState(user?.apellidos || "");
    const [telefono, setTelefono] = useState(user?.telefono || "");
    const [tipoCliente, setTipoCliente] = useState(user?.tipo_cliente || "empresa");
    const [logoUrl, setLogoUrl] = useState(user?.logo_url || "");
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [ok, setOk] = useState(false);
    const [granted, setGranted] = useState(false);
    const [error, setError] = useState("");
    const fileInputRef = useRef(null);

    // Mantener sincronizado con el usuario tras refresh
    useEffect(() => { if (user?.logo_url !== undefined) setLogoUrl(user.logo_url || ""); }, [user?.logo_url]);

    const isAsesoriaSelected = tipoCliente === "asesoria";
    const pendingWelcome = user && !user.welcome_credits_granted;

    async function save(e) {
        e.preventDefault();
        setError(""); setOk(false); setSaving(true);
        try {
            const { data } = await api.put("/auth/me", { name, apellidos, telefono, tipo_cliente: tipoCliente });
            if (refresh) await refresh();
            setOk(true);
            if (data?.welcome_credits_granted) setGranted(true);
            setTimeout(() => setOk(false), 3000);
        } catch (e) {
            setError(formatApiError(e, "No se pudo guardar el perfil"));
        } finally {
            setSaving(false);
        }
    }

    async function handleLogoFile(file) {
        if (!file) return;
        setError("");
        if (!/\.(png|jpe?g|webp|svg)$/i.test(file.name)) {
            setError("Formato no soportado. Usa PNG, JPG, WEBP o SVG.");
            return;
        }
        if (file.size > 500 * 1024) {
            setError("El logo supera el máximo permitido (500 KB). Redúcelo antes de subirlo.");
            return;
        }
        setUploadingLogo(true);
        try {
            const dataUrl = await new Promise((resolve, reject) => {
                const r = new FileReader();
                r.onload = () => resolve(r.result);
                r.onerror = reject;
                r.readAsDataURL(file);
            });
            const { data } = await api.put("/profile/logo", { logo_data_url: dataUrl });
            setLogoUrl(data.logo_url || "");
            if (refresh) await refresh();
            setOk(true);
            setTimeout(() => setOk(false), 3000);
        } catch (e) {
            setError(formatApiError(e, "No se pudo subir el logo"));
        } finally {
            setUploadingLogo(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }

    async function removeLogo() {
        if (!confirm("¿Quitar el logo actual?")) return;
        setUploadingLogo(true);
        try {
            await api.put("/profile/logo", { logo_data_url: "" });
            setLogoUrl("");
            if (refresh) await refresh();
        } catch (e) {
            setError(formatApiError(e, "No se pudo eliminar el logo"));
        } finally {
            setUploadingLogo(false);
        }
    }

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <Navbar />
            <main className="flex-1 max-w-3xl mx-auto w-full px-4 md:px-8 py-10 md:py-14">
                <button onClick={() => nav(-1)} className="text-xs uppercase tracking-widest text-[var(--gf-text-muted)] hover:text-[var(--gf-navy)] inline-flex items-center gap-1 mb-6" data-testid="back-btn">
                    <ArrowLeft size={12} /> Volver
                </button>
                <div className="mb-8">
                    <div className="micro-label mb-2">— Panel de control</div>
                    <h1 className="serif text-3xl md:text-4xl text-[var(--gf-navy)] flex items-center gap-3">
                        <UserIcon size={28} weight="light" />
                        Tu perfil
                    </h1>
                    <p className="text-sm text-[var(--gf-text-secondary)] mt-2">
                        Ajusta tu nombre visible, tipo de cliente y el logo que aparecerá en los informes.
                    </p>
                </div>

                <form onSubmit={save} className="gf-card space-y-5" data-testid="profile-form">
                    {pendingWelcome && !granted && (
                        <div className="border-l-4 border-l-[var(--gf-gold)] bg-[var(--gf-gold)]/10 p-4 text-sm flex items-start gap-3" data-testid="welcome-credits-banner">
                            <Gift size={20} weight="fill" className="text-[var(--gf-gold)] flex-shrink-0 mt-0.5" />
                            <div>
                                <strong className="text-[var(--gf-navy)]">Te esperan 50 créditos de bienvenida.</strong>{" "}
                                Completa tu nombre, apellidos y teléfono y guarda los cambios para recibirlos en tu saldo (1 € = 1 crédito).
                            </div>
                        </div>
                    )}
                    {granted && (
                        <div className="border-l-4 border-l-emerald-500 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-start gap-3" data-testid="welcome-credits-granted">
                            <Gift size={20} weight="fill" className="flex-shrink-0 mt-0.5" />
                            <div><strong>¡50 créditos añadidos a tu saldo!</strong> Ya puedes generar tu primer Dictamen Pre-Bancario o Informe Financiero.</div>
                        </div>
                    )}
                    <div>
                        <label className="gf-label">Email</label>
                        <input className="gf-input" value={user?.email || ""} disabled data-testid="profile-email" />
                        <p className="text-[11px] text-[var(--gf-text-muted)] mt-1">El email es la identidad de tu cuenta y no puede cambiarse. Contacta soporte si necesitas migrar.</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="gf-label">Nombre</label>
                            <input className="gf-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" placeholder="Ej: Julio" />
                        </div>
                        <div>
                            <label className="gf-label">Apellidos</label>
                            <input className="gf-input" value={apellidos} onChange={(e) => setApellidos(e.target.value)} data-testid="profile-apellidos" placeholder="Ej: García López" />
                        </div>
                    </div>
                    <div>
                        <label className="gf-label">Teléfono</label>
                        <input className="gf-input" type="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} data-testid="profile-telefono" placeholder="Ej: 600 123 456" />
                    </div>

                    <div>
                        <label className="gf-label">Tipo de cliente</label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="profile-tipo-group">
                            {[
                                { v: "empresa", t: "Empresa", d: "Una o varias empresas propias" },
                                { v: "autonomo", t: "Autónomo", d: "Facturación personal" },
                                { v: "asesoria", t: "Asesoría", d: "Gestiono clientes de terceros" },
                            ].map((o) => (
                                <label key={o.v}
                                    className={`cursor-pointer border p-3 text-sm transition-all ${tipoCliente === o.v ? "border-[var(--gf-gold)] bg-[var(--gf-gold)]/10" : "border-[var(--gf-border)] hover:border-[var(--gf-text-secondary)]"}`}
                                    data-testid={`profile-tipo-${o.v}`}
                                >
                                    <input type="radio" name="tipoCliente" value={o.v} checked={tipoCliente === o.v} onChange={(e) => setTipoCliente(e.target.value)} className="sr-only" />
                                    <div className="font-semibold text-[var(--gf-navy)]">{o.t}</div>
                                    <div className="text-xs text-[var(--gf-text-secondary)] mt-1">{o.d}</div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="gf-label flex items-center gap-2">
                            <Buildings size={14} weight="bold" />
                            Logo de la asesoría
                        </label>
                        {!isAsesoriaSelected ? (
                            <div className="mt-1 border border-dashed border-[var(--gf-border)] bg-[var(--gf-subtle)] p-4 text-xs text-[var(--gf-text-muted)]" data-testid="logo-locked">
                                Selecciona <strong>Asesoría</strong> como tipo de cliente para subir tu logo.
                                Aparecerá en los informes que envíes a tus clientes.
                            </div>
                        ) : (
                            <div className="mt-1 space-y-3" data-testid="logo-upload-block">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                    className="gf-input"
                                    onChange={(e) => handleLogoFile(e.target.files?.[0])}
                                    disabled={uploadingLogo}
                                    data-testid="logo-file-input"
                                />
                                <p className="text-[11px] text-[var(--gf-text-muted)]">
                                    Formatos: PNG, JPG, WEBP o SVG. Máximo 500 KB. Recomendado: PNG con fondo transparente.
                                </p>
                                {uploadingLogo && (
                                    <div className="text-xs text-[var(--gf-text-secondary)] flex items-center gap-2">
                                        <span className="elegant-loader"><span /><span /><span /></span>
                                        Procesando logo…
                                    </div>
                                )}
                                {logoUrl && (
                                    <div className="flex items-center gap-3 border border-[var(--gf-border)] p-3 bg-[var(--gf-subtle)]">
                                        <img
                                            src={logoUrl}
                                            alt="Logo asesoría"
                                            className="h-16 w-auto object-contain bg-white p-1"
                                            data-testid="logo-preview"
                                        />
                                        <div className="flex-1 text-xs text-[var(--gf-text-secondary)]">
                                            Este logo aparecerá en la cabecera de los informes generados para tus clientes.
                                        </div>
                                        <button
                                            type="button"
                                            onClick={removeLogo}
                                            disabled={uploadingLogo}
                                            className="text-xs text-[var(--gf-danger)] hover:underline inline-flex items-center gap-1"
                                            data-testid="logo-remove-btn"
                                        >
                                            <Trash size={14} /> Quitar
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {error && <div className="text-sm text-[var(--gf-danger)] bg-red-50 border-l-2 border-[var(--gf-danger)] px-3 py-2">{error}</div>}
                    {ok && <div className="text-sm text-emerald-800 bg-emerald-50 border-l-2 border-emerald-500 px-3 py-2" data-testid="profile-ok">Perfil actualizado.</div>}

                    <div className="flex justify-end pt-3 border-t border-[var(--gf-border)]">
                        <button type="submit" disabled={saving} className="btn-gold" data-testid="profile-submit">
                            <FloppyDisk size={14} weight="bold" /> {saving ? "Guardando…" : "Guardar cambios"}
                        </button>
                    </div>
                </form>
            </main>
            <Footer />
        </div>
    );
}
